<script setup lang="ts">
import { CalendarCheck } from 'lucide-vue-next'
import type { Icons } from './base/BaseIcon.vue'
import UpsellModuleIcon from './UpsellModuleIcon.vue'
const props = defineProps<{
  module: 'e-reputation' | 'pongo-suite' | null
  show: boolean
}>()

const emit = defineEmits<{
  close: []
}>()

const { t } = useI18n()
const { send } = useEvent()
const { user } = useLocalStorage()

const eReputationFeatures: { icon: Icons; titleKey: string; descriptionKey: string }[] = [
  {
    icon: 'HeroGlobeAltIconOutline',
    titleKey: 'pages.upsell.eReputation.features.feature1.title',
    descriptionKey: 'pages.upsell.eReputation.features.feature1.description'
  },
  {
    icon: 'HeroChartBarSquareIconOutline',
    titleKey: 'pages.upsell.eReputation.features.feature2.title',
    descriptionKey: 'pages.upsell.eReputation.features.feature2.description'
  },
  {
    icon: 'IconoirBubbleStar',
    titleKey: 'pages.upsell.eReputation.features.feature3.title',
    descriptionKey: 'pages.upsell.eReputation.features.feature3.description'
  },
  {
    icon: 'IconoirDonate',
    titleKey: 'pages.upsell.eReputation.features.feature4.title',
    descriptionKey: 'pages.upsell.eReputation.features.feature4.description'
  }
]

const retentionFeatures: { icon: Icons; titleKey: string; descriptionKey: string }[] = [
  {
    icon: 'HeroUserGroupIconOutline',
    titleKey: 'pages.upsell.retention.features.feature1.title',
    descriptionKey: 'pages.upsell.retention.features.feature1.description'
  },
  {
    icon: 'HeroGiftIconOutline',
    titleKey: 'pages.upsell.retention.features.feature2.title',
    descriptionKey: 'pages.upsell.retention.features.feature2.description'
  },
  {
    icon: 'HeroChatBubbleLeftRightIconOutline',
    titleKey: 'pages.upsell.retention.features.feature3.title',
    descriptionKey: 'pages.upsell.retention.features.feature3.description'
  },
  {
    icon: 'HeroLinkIconOutline',
    titleKey: 'pages.upsell.retention.features.feature4.title',
    descriptionKey: 'pages.upsell.retention.features.feature4.description'
  }
]

const titleKey = computed(() => {
  return props.module === 'e-reputation' ? 'pages.upsell.eReputation.title' : 'pages.upsell.retention.title'
})

const descriptionKey = computed(() => {
  return props.module === 'e-reputation' ? 'pages.upsell.eReputation.description' : 'pages.upsell.retention.description'
})

const priceKey = computed(() => {
  return props.module === 'e-reputation' ? 'pages.upsell.eReputation.price' : 'pages.upsell.retention.price'
})

const features = computed(() => {
  return props.module === 'e-reputation' ? eReputationFeatures : retentionFeatures
})

const handleClose = () => {
  emit('close')
}

const handleOpenCalendar = () => {
  switch (props.module) {
    case 'e-reputation':
      send('segment', `${props.module}.upsellModal.calendarOpened`, {
        merchantPublicId: user.value.merchant.publicId,
        userPublicId: user.value.publicId
      })
      window.open('https://meetings.hubspot.com/cassiopee-lacarriere/e-reputation', '_blank')
      break
    case 'pongo-suite':
      send('segment', `${props.module}.upsellModal.calendarOpened`, {
        merchantPublicId: user.value.merchant.publicId,
        userPublicId: user.value.publicId
      })
      window.open('https://meetings.hubspot.com/cassiopee-lacarriere/crm-fidelite', '_blank')
      break
  }
}

watch(
  () => props.show,
  (newVal) => {
    if (newVal === true) {
      send('segment', `${props.module}.upsellModal.opened`, {
        merchantPublicId: user.value.merchant.publicId,
        userPublicId: user.value.publicId
      })
    }
  }
)
</script>

<template>
  <BaseModal size="3xl" :is-open="show" :show-footer="false" class="rounded-3xl!" @close="handleClose">
    <div class="gap-10! flex flex-col">
      <div class="flex justify-center mt-4">
        <BaseChip
          color="violet"
          size="lg"
          icon="HeroLockClosedIconOutline"
          :text="t('pages.upsell.unavailableWithCurrentOffer')"
          text-classes="font-medium"
        />
      </div>
      <div class="flex-1 bg-content-bg border-border border rounded-2xl flex flex-col">
        <div class="dotted-background flex h-full flex-col gap-4 p-6">
          <div class="flex items-center justify-center">
            <UpsellModuleIcon :module="module" />
          </div>
          <div class="flex flex-col gap-2">
            <h1 class="text-3xl font-bold text-center">{{ t(titleKey) }}</h1>

            <p class="text-center text-foreground-secondary">
              {{ t(descriptionKey) }}
            </p>

            <p class="text-center text-foreground-secondary">
              {{ t('pages.upsell.priceLine', { price: t(priceKey) }) }}
            </p>
          </div>
        </div>
      </div>

      <div class="flex flex-col sm:flex-row gap-10">
        <div v-for="feature in features.slice(0, 2)" :key="feature.titleKey" class="flex flex-row flex-1 gap-4">
          <div class="flex flex-row size-10 min-w-10 items-center justify-center bg-primary/5 rounded-xl">
            <BaseIcon :name="feature.icon" class="size-6 text-primary" />
          </div>
          <div class="flex flex-col gap-1">
            <h2 class="text-base font-semibold">{{ t(feature.titleKey) }}</h2>
            <p class="text-foreground-secondary text-sm text-balance">{{ t(feature.descriptionKey) }}</p>
          </div>
        </div>
      </div>

      <div class="flex flex-col sm:flex-row gap-10">
        <div v-for="feature in features.slice(2, 4)" :key="feature.titleKey" class="flex flex-row flex-1 gap-4">
          <div class="flex flex-row size-10 min-w-10 items-center justify-center bg-primary/5 rounded-xl">
            <BaseIcon :name="feature.icon" class="size-6 text-primary" />
          </div>
          <div class="flex flex-col gap-1">
            <h2 class="text-base font-semibold">{{ t(feature.titleKey) }}</h2>
            <p class="text-foreground-secondary text-sm text-balance">{{ t(feature.descriptionKey) }}</p>
          </div>
        </div>
      </div>
      <div class="p-4 sm:p-5 bg-muted border-border border rounded-2xl flex flex-row gap-4 items-center">
        <div class="flex size-11 shrink-0 items-center justify-center rounded-xl bg-primary/5">
          <CalendarCheck class="size-5 text-primary" />
        </div>
        <div class="flex flex-col gap-1">
          <p class="text-sm font-semibold text-primary">
            {{ t('pages.upsell.activationInfo.title') }}
          </p>
          <p class="text-sm text-foreground-secondary">
            {{ t('pages.upsell.activationInfo.description') }}
          </p>
        </div>
      </div>

      <div class="flex flex-col gap-3 items-center justify-center">
        <Button
          variant="default-primary"
          class="w-full text-base! flex flex-row gap-2 items-center justify-center"
          @click="handleOpenCalendar"
        >
          {{ t('pages.upsell.activationSuccess.chooseSlot') }}
          <BaseIcon name="HeroCalendarIconOutline" class="size-5" />
        </Button>
      </div>
    </div>
  </BaseModal>
</template>

<style scoped>
.custom-shadow {
  box-shadow:
    0 4px 4px 0 rgba(0, 0, 0, 0.25),
    0 2.5px 15px 0 rgba(6, 6, 6, 0.15);
}

.custom-purple-shadow {
  box-shadow: 0 8px 24px 0 rgba(124, 58, 237, 0.25);
}
</style>
